import { useEffect, useState } from 'react'

import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

import type { AcknowledgmentSignerOption } from './types'

type GlobalPartnerContactRecord = {
  ContactID: string
  Email?: string
  FirstName?: string
  LastName?: string
  PartnerName?: string
  PR_HP_ROLE?: string
}

const buildFullName = (firstName?: string, lastName?: string) =>
  [firstName, lastName].filter(Boolean).join(' ').trim() || '—'

const dedupeOptions = (options: AcknowledgmentSignerOption[]) =>
  Array.from(
    new Map(
      options.map((option) => [
        `${option.email}:${option.contactId ?? option.role ?? option.id}`,
        option,
      ])
    ).values()
  )

export const useGlobalPartnerSignerOptions = (partnerId: string, show: boolean) => {
  const [options, setOptions] = useState<AcknowledgmentSignerOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isActive = true

    const loadOptions = async () => {
      if (!show || !partnerId.startsWith('GP')) {
        setOptions([])
        setError(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const crud = CrudStore.get(
          'JBPGlobalPartnerPartnersContacts',
          `acknowledgment-global-partner-options:${partnerId}`
        )

        crud.setFilter({
          logic: Logic.AND,
          filters: [
            {
              field: 'GlobalPartnerId',
              operator: Operator.EQUAL,
              value: partnerId,
            },
          ],
        })

        crud.setSort([
          {
            field: 'PrimaryPartner',
            dir: 'desc',
          },
          {
            field: 'LastName',
            dir: 'asc',
          },
        ])

        crud.setPaging({
          page: 0,
          pageSize: 1000,
        })

        const response = await crud.fetch({ force: true })
        const items = response.items as GlobalPartnerContactRecord[]

        if (!isActive) {
          return
        }

        setOptions(
          dedupeOptions(
            items
              .filter((item) => Boolean(item.Email))
              .map((item) => ({
                id: `${item.ContactID}-${item.Email}`,
                contactId: item.ContactID,
                email: item.Email ?? '',
                firstName: item.FirstName ?? '',
                lastName: item.LastName ?? '',
                fullName: buildFullName(item.FirstName, item.LastName),
                secondaryLabel: item.PR_HP_ROLE,
                tertiaryLabel: item.PartnerName,
              }))
          )
        )
      } catch (e: any) {
        if (isActive) {
          setOptions([])
          setError(e?.message ?? 'Failed to load contacts')
        }
      } finally {
        if (isActive) {
          setIsLoading(false)
        }
      }
    }

    void loadOptions()

    return () => {
      isActive = false
    }
  }, [partnerId, show])

  return {
    options,
    isLoading,
    error,
  }
}
